import moment from "moment";

const normalizeNumber = (value, fallback = 0) => {
    const normalized = typeof value === "string" ? value.replace(/"/g, "") : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeInteger = (value, fallback = 0) => {
    const normalized = typeof value === "string" ? value.replace(/"/g, "") : value;
    const parsed = parseInt(normalized, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseJsonObject = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object") return value;

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : fallback;
    } catch (e) {
        return fallback;
    }
};

const parseJsonValue = (value, fallback = null) => {
    if (value === undefined || value === null || value === "") return fallback;
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
};

const normalizePriceModel = (value) => {
    const parsed = parseJsonValue(value, "new");
    return String(parsed || "new").replace(/"/g, "") === "old" ? "old" : "new";
};

const getSettingValue = (settings, name) => {
    if (Array.isArray(settings)) {
        return settings.find((setting) => setting.setting == name)?.value;
    }

    if (settings && typeof settings === "object") {
        return settings[name];
    }

    return undefined;
};

const normalizeClientBillingSettings = (client) => {
    const settings = client?.settings;

    return {
        minimumLicenses: normalizeInteger(getSettingValue(settings, "minimumLicenses"), 0),
        modulePrice: normalizeNumber(getSettingValue(settings, "modulePrice"), 0),
        modulePrices: parseJsonObject(getSettingValue(settings, "modulePrices"), {}),
        priceModel: normalizePriceModel(getSettingValue(settings, "priceModel")),
        billingInterval: Math.max(1, normalizeInteger(getSettingValue(settings, "billingInterval"), 1)),
        partnerProvision: normalizeNumber(getSettingValue(settings, "partnerProvision"), 0),
        implementationFee: normalizeNumber(getSettingValue(settings, "implementationFee"), 0),
        implementationFeeCommission: normalizeNumber(getSettingValue(settings, "implementationFeeCommission"), 0),
    };
};

const normalizeInvoiceMetadata = (metadata = {}) => {
    const normalizedModules = Array.isArray(metadata.clientModules)
        ? metadata.clientModules.map((module) => ({
            ...module,
            count: normalizeInteger(module?.count, 0),
        }))
        : [];

    return {
        ...metadata,
        modulePrices: parseJsonObject(metadata.modulePrices, {}),
        priceModel: normalizePriceModel(metadata.priceModel),
        implementationFee: normalizeNumber(metadata.implementationFee, 0),
        implementationFeeCommission: normalizeNumber(metadata.implementationFeeCommission, 0),
        clientModules: normalizedModules,
        minimumLicenses: normalizeInteger(metadata.minimumLicenses, 0),
        nrMembers: normalizeInteger(metadata.nrMembers, 0),
        totalModules: normalizeInteger(metadata.totalModules, 0),
        billingInterval: Math.max(1, normalizeInteger(metadata.billingInterval, 1)),
    };
};

const parseInvoiceData = (invoice) => {
    if (!invoice) return null;

    const parsed = parseJsonObject(invoice.invoiceData, null);
    if (!parsed) return null;

    return {
        ...parsed,
        value: normalizeNumber(parsed.value, 0),
        commission: normalizeNumber(parsed.commission, 0),
        commissionPercentage: normalizeNumber(parsed.commissionPercentage, 0),
        outstandingCommission: normalizeNumber(parsed.outstandingCommission, 0),
        invoiceMetadata: normalizeInvoiceMetadata(parsed.invoiceMetadata || {}),
    };
};

const addImplementationFee = (client, invoices) => {
    if (!client || !invoices) {
        return false;
    }

    const firstInvoice = !invoices.some((invoice) => invoice.clientId == client.ID);
    if (!firstInvoice) {
        return false;
    }

    const billing = normalizeClientBillingSettings(client);

    if (billing.implementationFee === 0) {
        return false;
    }

    return {
        sum: billing.implementationFee,
        commission: billing.implementationFee * (billing.implementationFeeCommission / 100),
    };
};

const calculateEstimatedValue = (client, invoices, members, clientModules = null, billingInterval) => {
    if (!client || !invoices || !members) return false;

    let totalModules = clientModules ? clientModules.reduce((sum, module) => sum + (normalizeInteger(module?.count, 0)), 0) : sumAllModules(client, members);

    if (isNaN(totalModules)) {
        totalModules = 0;
    }

    const billing = normalizeClientBillingSettings(client);
    const interval = Math.max(1, normalizeInteger(billingInterval, billing.billingInterval));

    if (billing.priceModel === "new") {
        let sum = 0;

        if (totalModules < billing.minimumLicenses) {
            sum += billing.minimumLicenses * billing.modulePrice;
        } else if (clientModules) {
            clientModules.forEach((module) => {
                sum += normalizeInteger(module?.count, 0) * (billing.modulePrices[module.name] || billing.modulePrice || 0);
            });
        } else {
            const clientMod = client.modules?.split(",");
            if (!clientMod) return 0;

            clientMod.forEach((module) => {
                const nrMembers = members.filter((member) => {
                    if (!member?.modules) {
                        return false;
                    }
                    return member.modules.some((m) => m.module == module && m.clientId == client.ID);
                }).length;

                sum += nrMembers * (billing.modulePrices[module] || billing.modulePrice || 0);
            });
        }

        const implementationFee = addImplementationFee(client, invoices);

        return {
            sum,
            implementationFee: implementationFee ? Number(implementationFee.sum) : 0,
            total: sum + (implementationFee ? Number(implementationFee.sum) : 0),
        };
    }

    let sum = 0;
    const clientModuleNames = client.modules?.split(",");
    if (!clientModuleNames) return 0;

    clientModuleNames.forEach((module) => {
        const modulePrice = [parseFloat(billing.modulePrices[module]), parseFloat(billing.modulePrice)].find((n) => !isNaN(n)) ?? 0;
        sum += client.members > billing.minimumLicenses ? (modulePrice * client.members || 0) : (billing.minimumLicenses * modulePrice || 0);
    });

    sum = sum * interval;

    const implementationFee = addImplementationFee(client, invoices);

    return {
        sum,
        implementationFee: implementationFee ? Number(implementationFee.sum) : 0,
        total: sum + (implementationFee ? Number(implementationFee.sum) : 0),
    };
};

const calculatePartnerProvision = (client, invoices, members, newModules, billingInterval) => {
    if (!client || !client.settings) return false;

    const billing = normalizeClientBillingSettings(client);
    let sum = 0;

    const estimatedValue = calculateEstimatedValue(client, invoices, members, newModules, billingInterval);
    const standardProvision = client.partner
        ? Math.round((billing.partnerProvision * estimatedValue.sum / 100) * 100) / 100
        : 0;

    sum += standardProvision;

    const implementationFee = addImplementationFee(client, invoices);

    if (implementationFee && client.partner && billing.partnerProvision > 0) {
        const implementationFeeCommission = parseInt(implementationFee.commission, 10) || 0;
        sum += implementationFeeCommission;
    }

    return sum;
};

const sumAllModules = (client, members, clientModules = null) => {
    if (!client || !members) return 0;
    let sum = 0;

    if (clientModules) {
        clientModules.forEach((module) => {
            sum += normalizeInteger(module?.count, 0);
        });

        return sum;
    }

    try {
        client.modules.split(",").forEach((module) => {
            sum += members.filter((member) => member?.modules?.some((m) => m.module == module && m.clientId == client.ID)).length;
        });
    } catch (e) {
        return 0;
    }

    return parseInt(sum, 10) || 0;
};

const calculateInvoiceStatistics = (clients, invoices) => {
    if (!clients || !invoices) return false;

    let statisticsPerMonth = {};

    invoices.forEach((invoice) => {
        const data = parseInvoiceData(invoice);
        const metadata = data?.invoiceMetadata;

        if (!metadata) return;

        const interval = metadata.billingInterval || 1;
        const valuePerMonth = (data.value || 0) / interval;
        const priceModel = metadata.priceModel || "new";

        const start = moment.unix(invoice.invoiceDate).startOf('month');

        for (let i = 0; i < interval; i++) {
            const monthKey = start.clone().add(i, 'month').format('YYYY-MM');
            if (!statisticsPerMonth[monthKey]) {
                statisticsPerMonth[monthKey] = {
                    totalLicenses: { rolfing: 0, actionplan: 0, reflection: 0, development: 0 },
                    pulseLicenses: 0,
                    totalValue: 0
                };
            }

            statisticsPerMonth[monthKey].totalValue += valuePerMonth;

            if (priceModel === "new" && metadata.clientModules) {
                metadata.clientModules.forEach((module) => {
                    const licensesPerMonth = normalizeInteger(module.count, 0) / interval;

                    if (module.name === "pulse") {
                        statisticsPerMonth[monthKey].pulseLicenses += licensesPerMonth;
                        return;
                    }

                    if (statisticsPerMonth[monthKey].totalLicenses[module.name] !== undefined) {
                        statisticsPerMonth[monthKey].totalLicenses[module.name] += licensesPerMonth;
                    }
                });
            } else if (priceModel === "old") {
                const nrMembers = metadata.nrMembers || 0;
                const minimumLicenses = metadata.minimumLicenses || 0;
                const moduleNames = Object.keys(statisticsPerMonth[monthKey].totalLicenses);

                moduleNames.forEach((moduleName) => {
                    if (metadata.modulePrices && metadata.modulePrices[moduleName]) {
                        const licensesPerMonth = (nrMembers > minimumLicenses ? nrMembers : minimumLicenses) / interval;

                        if (moduleName === "pulse") {
                            statisticsPerMonth[monthKey].pulseLicenses += licensesPerMonth;
                            return;
                        }

                        statisticsPerMonth[monthKey].totalLicenses[moduleName] += licensesPerMonth;
                    }
                });
            }
        }
    });

    Object.keys(statisticsPerMonth).forEach((monthKey) => {
        const totalLicenses = Object.values(statisticsPerMonth[monthKey].totalLicenses).reduce((a, b) => a + b, 0);
        statisticsPerMonth[monthKey].avgValuePerLicense = totalLicenses > 0 ? statisticsPerMonth[monthKey].totalValue / totalLicenses : 0;
    });

    const now = moment();
    statisticsPerMonth = Object.fromEntries(
        Object.entries(statisticsPerMonth).filter(([monthKey]) =>
            moment(monthKey + "-01").isSameOrBefore(now, "month")
        )
    );

    return statisticsPerMonth;
};

export {
    addImplementationFee,
    calculateEstimatedValue,
    calculatePartnerProvision,
    sumAllModules,
    calculateInvoiceStatistics,
    normalizeClientBillingSettings,
    normalizeInvoiceMetadata,
    parseInvoiceData,
};
